import PropTypes from "prop-types";

export const newSlice = () => ({
    keys: [],
    byKey: {},
});

export const sliceTypeBuilder = valueType => PropTypes.shape({
    keys: PropTypes.arrayOf(
        PropTypes.string,
    ).isRequired,
    byKey: PropTypes.objectOf(
        valueType,
    ).isRequired,
});

/*
 * Slices are a simple indexed collection represented as an Object. The object
 * has two properties: 'keys', and 'byKey'. Every item in the collection must
 * have an key, and they are listed in order in the 'keys' array. The 'byKey'
 * property contains an object with keys corresponding to the items' keys, and
 * values of the items themselves. This is almost exactly analogous to a
 * clustered PRIMARY KEY in a traditional relational database. The main
 * difference is that the order of the original array is preserved; the items
 * are not ordered by their keys as is normally (though implicitly) the case in
 * a relational database. This makes slices suitable for storing custom-ordered
 * lists (e.g., search results).
 *
 * The 'toSlice' function will take an array of items and create a slice from
 * it, by default using the 'id' propery of each item as the key, and leaving
 * the items untouched. The property to use for the slice may be customized, as
 * well as a "transform" function to mutate the items in the resulting slice.
 * "transform"'s type is `(A, K) -> B`.
 *
 * The 'fromSlice' function will take a slice and return it to it's original
 * array form, preserving order. It also accepts a "transform" function to
 * mutate the items in the resulting array. "transform"'s type is `(B, K) -> A`.
 */

export const toSlice = (items, keyProp = "id", transform = undefined) => {
    if (items == null) {
        return null;
    }
    if (!(items instanceof Array)) {
        throw new Error("Slices can only be constructed from an array");
    }
    if (typeof keyProp !== "string") {
        throw new Error("toSlice requires a string key property");
    }
    if (transform && typeof transform !== "function") {
        throw new Error("transform, if supplied, must be a function");
    }
    return items.reduce((slice, it) => {
        if (!it.hasOwnProperty(keyProp)) {
            throw new Error(`Item found with no '${keyProp}' property`);
        }
        const key = it[keyProp];
        if (!slice.byKey.hasOwnProperty(key)) {
            slice.keys.push(key);
        }
        slice.byKey[key] = transform ? transform(it, key) : it;
        return slice;
    }, newSlice());
};

export const fromSlice = (slice, transform = undefined) => {
    if (slice == null) {
        return slice;
    }
    if (transform && typeof transform !== "function") {
        throw new Error("transform, if supplied, must be a function");
    }
    if (!slice.hasOwnProperty("byKey") || !slice.hasOwnProperty("keys")) {
        throw new Error("fromSlice requires a valid slice");
    }
    return slice.keys.map(key => {
        const it = slice.byKey[key];
        return transform ? transform(it, key) : it;
    });
};

export const concatSlice = (...slices) => {
    const byKey = slices
        .filter(s => s)
        .map(s => s.byKey)
        .reduce((allByKey, sliceByKey) => ({
        ...allByKey,
        ...sliceByKey,
    }), {});
    return {
        keys: Object.keys(byKey),
        byKey,
    };
};



// WEBPACK FOOTER //
// ./app/data/slices.js